import { useEffect, useRef, useState } from 'react';
import { useDemo } from './Demo.tsx';
import { useInView, useReducedMotion } from '../motion.ts';

/**
 * The one part of this page that is not a picture of the product.
 *
 * "Take the demo" opens the real one — Persivo's own agent, on a clone of
 * Persivo's own console, in a cross-origin frame of the same stage host a
 * customer would embed. The chip is carried in with it and spoken as the
 * visitor's own opening line, so the tour a security reviewer gets is not the
 * tour someone asking about admin setup gets. See `src/demo.ts`.
 *
 * The chips are approved marketing copy. They are also the keys of `OPENERS` in
 * `demo.ts` — change one here and the opener it carries stops matching, which
 * fails silently into a demo that opens on nothing in particular.
 *
 * The headline is the chips, said out loud. "A better way to say →" and then the
 * way each kind of buyer actually says it — the last words swap for the next ask,
 * plainly: the old ones lift away and the new ones come up under them. (It was a
 * glyph scramble first; mid-swap it read as a glitch, so it is a plain swap now.)
 * Until somebody chooses, it walks the three in turn and the chip that matches
 * lights with it; the moment somebody chooses — or lingers over the card — it
 * stops, and what the headline says is what "Meet the agent" will open with.
 */
const PURPOSES = ['Evaluating it', 'Security review', 'Admin setup'] as const;
const SAYS = ['show me.', 'is it secure?', 'how do I set it up?'] as const;

export function TryOut() {
  const [i, setI] = useState(0);
  const [prev, setPrev] = useState(-1);
  const [stopped, setStopped] = useState(false);
  const section = useRef<HTMLElement>(null);
  const launch = useRef<HTMLButtonElement>(null);
  const demo = useDemo();
  const reduced = useReducedMotion();
  const inView = useInView(section, 0.35);
  const purpose = PURPOSES[i]!;
  const go = (n: number) => { setPrev(i); setI(n); };

  useEffect(() => {
    if (!inView || stopped || reduced) return;
    const t = window.setTimeout(() => { setPrev(i); setI((i + 1) % PURPOSES.length); }, 3400);
    return () => clearTimeout(t);
  }, [inView, stopped, reduced, i]);

  return <section className="demo-section kinetic" id="try" ref={section} data-in={inView ? '' : undefined}>
    <div className="wrap demo-inner">
      <div className="kinetic-head">
        <p className="eyebrow">YOUR TURN</p>
        <h2 aria-label={`A better way to say “${SAYS[i]}”`}>
          <span className="k-a" aria-hidden="true">A better way to say</span>
          <span className="k-b" aria-hidden="true">
            <svg className="k-arrow" key={i} viewBox="0 0 86 56" aria-hidden="true"><path pathLength="1" d="M3 28H80M56 5l24 23-24 23"/></svg>
            <span className="k-words">{SAYS.map((w, n) => <span key={w} className="k-word" data-state={n === i ? 'now' : n === prev ? 'out' : 'wait'}>“{w}”</span>)}</span>
          </span>
        </h2>
        <p className="k-sub">Bring a question.<br />Let the product make its case.</p>
      </div>
      <div className="demo-choice" onPointerEnter={() => setStopped(true)} onFocus={() => setStopped(true)}>
        <img className="demo-orb" src="/agent-thinking.svg" alt=""/>
        <h3>What brought you here?</h3>
        <div className="picks" role="group" aria-label="Choose your demo focus">{PURPOSES.map((p, n) => <button key={p} className={'pick-chip' + (purpose === p ? ' on' : '')} aria-pressed={p === purpose} onClick={() => { setStopped(true); if (n !== i) go(n); }}>{p}</button>)}</div>
        <button ref={launch} className="btn solid" onPointerEnter={demo.warm} onFocus={demo.warm} onClick={() => demo.open({ purpose, from: launch.current })}>Meet the agent <span aria-hidden="true">↗</span></button>
        <p className="try-note">A guided product tour. It starts with your name, work email and role, so the team can write back.</p>
      </div>
    </div>
  </section>;
}
